import { stringValue } from 'vega-lib';
import { isNumber } from 'lodash';

function getOptionValue(customisations, option, defaultValue) {
  const o = customisations && customisations[option];
  return o && o.enabled ? o.value : defaultValue;
}

function useGrouping({ numberGrouping = { enabled: true } }) {
  return numberGrouping.enabled;
}

/**
 * Generate a expression to be used in Vega spec to format the numeric values.
 *
 * @param {Object}    customisations    customisations object for the channel
 * @param {Boolean}   withDecimals      decimals setting will be ignored if false
 * @param {Boolean}   withSpacePadding  if true, puts spacing before and after the formatted value
 * @param {String}    field             name of the field on the domain data
 *                                      for example on axis or legend
 * @returns {String}                    Vega format expression
 */
/* eslint-disable complexity */
export function generateVegaNumberFormatExpression(
  customisations,
  withDecimals = false,
  withSpacePadding = false,
  field = 'value'
) {
  // return value as is if number formatting options are not applicable
  if (!getOptionValue(customisations, 'numberFormatting')) {
    return `datum["${field}"]`;
  }

  let decimals =
    withDecimals && getOptionValue(customisations, 'numberDecimals');
  let multiplier = getOptionValue(customisations, 'numberMultiplier');
  let prefix = getOptionValue(customisations, 'numberPrefix');
  let suffix = getOptionValue(customisations, 'numberSuffix');

  // Vega 4 runs horizontal labels very close together. When this option is on we add spaces before
  // and after to prevent the labels being too close. If we later move to Vega 5 we can replace this
  // with the labelSeparation option.
  if (withSpacePadding) {
    prefix = ' ' + (prefix || '');
    suffix = (suffix || '') + ' ';
  }

  prefix = prefix ? `${stringValue(prefix)} + ` : '';
  suffix = suffix ? ` + ${stringValue(suffix)}` : '';
  decimals = decimals ? `.${decimals}f` : '';
  multiplier = multiplier && multiplier !== '1' ? ' * ' + multiplier : '';

  const rawValue = `datum['${field || 'value'}']`;

  const num = `${rawValue}${multiplier}`;
  const specifier = `${useGrouping(customisations) ? ',' : ''}${decimals}`;
  const format = prefix + `format(${num}, '${specifier}')` + suffix;
  return `${rawValue} === null ? 'null' : ${format}`;
}

/**
 * Format a number using the formatting specified in the provided customisations.
 *
 * @param {Number} value           Number to format
 * @param {Object} customisations  Customisations object
 * @param {String} locale          Locale used to format the number, default to system default
 *                                 Value is a string with a BCP 47 language tag
 * @returns {String}  formatted value string
 */
export function formatNumber(value, customisations, locale) {
  // return value as is if number formatting options are not applicable
  if (!getOptionValue(customisations, 'numberFormatting')) {
    return value;
  }

  if (value === null) {
    return `null`;
  }

  const decimals = getOptionValue(customisations, 'numberDecimals');
  const multiplier = parseFloat(
    getOptionValue(customisations, 'numberMultiplier', 1)
  );
  const prefix = getOptionValue(customisations, 'numberPrefix', '');
  const suffix = getOptionValue(customisations, 'numberSuffix', '');

  const fractionDigits = decimals && parseInt(decimals, 10);
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals ? fractionDigits : 0,
    maximumFractionDigits: decimals ? fractionDigits : 20,
    useGrouping: useGrouping(customisations),
  });
  return `${prefix}${formatter.format(
    value * parseFloat(multiplier)
  )}${suffix}`;
}
